import { reconcileScraperSessionProgress } from './reconcileScraperSessionProgress';

/**
 * Merge one scraper-progress (or batch item) payload into a session object.
 * Omitted fields in `data` leave existing session values unchanged.
 */
export function applyScraperProgressToSession(session, data) {
  if (!data?.sessionId || session.sessionId !== data.sessionId) {
    return session;
  }

  if (data.updateType === 'sourceAdded' && Array.isArray(data.sourceAdded) && data.sourceAdded.length > 0) {
    const existing = new Set((session.usernames || []).map((u) => String(u).toLowerCase()));
    const added = [];
    for (const u of data.sourceAdded) {
      const n = String(u || '').toLowerCase();
      if (n && !existing.has(n)) {
        existing.add(n);
        added.push(u);
      }
    }
    if (added.length === 0) {
      return reconcileScraperSessionProgress({
        ...session,
        progress: {
          ...session.progress,
          usernameStatus:
            data.usernameStatus !== undefined
              ? { ...(session.progress.usernameStatus || {}), ...data.usernameStatus }
              : session.progress.usernameStatus || {}
        }
      });
    }
    return reconcileScraperSessionProgress({
      ...session,
      usernames: [...(session.usernames || []), ...added],
      progress: {
        ...session.progress,
        totalUsernames: (session.progress.totalUsernames ?? session.usernames?.length ?? 0) + added.length,
        completedUsernames:
          data.current !== undefined ? data.current : session.progress.completedUsernames,
        usernameStatus:
          data.usernameStatus !== undefined
            ? { ...(session.progress.usernameStatus || {}), ...data.usernameStatus }
            : session.progress.usernameStatus || {}
      }
    });
  }

  if (data.updateType === 'totalFollowersCalculated') {
    return reconcileScraperSessionProgress({
      ...session,
      progress: {
        ...session.progress,
        totalFollowers:
          data.totalFollowers !== undefined ? data.totalFollowers : session.progress.totalFollowers,
        usernameStatus:
          data.usernameStatus !== undefined
            ? { ...(session.progress.usernameStatus || {}), ...data.usernameStatus }
            : session.progress.usernameStatus || {}
      },
      results: data.results !== undefined ? data.results : session.results || []
    });
  }

  return reconcileScraperSessionProgress({
    ...session,
    accountsInCooldown:
      data.accountsInCooldown !== undefined ? data.accountsInCooldown : session.accountsInCooldown || 0,
    progress: {
      ...session.progress,
      totalUsernames: data.total !== undefined ? data.total : session.progress.totalUsernames,
      completedUsernames:
        data.current !== undefined ? data.current : session.progress.completedUsernames,
      totalScraped: data.scraped !== undefined ? data.scraped : session.progress.totalScraped,
      totalStored: data.stored !== undefined ? data.stored : session.progress.totalStored,
      totalSkipped: data.skipped !== undefined ? data.skipped : session.progress.totalSkipped,
      totalDuplicates:
        data.duplicates !== undefined ? data.duplicates : session.progress.totalDuplicates || 0,
      totalFollowers:
        data.totalFollowers !== undefined ? data.totalFollowers : session.progress.totalFollowers || 0,
      totalBlacklisted:
        data.totalBlacklisted !== undefined ? data.totalBlacklisted : session.progress.totalBlacklisted || 0,
      blacklistedUsernames:
        data.blacklistedUsernames !== undefined
          ? data.blacklistedUsernames
          : session.progress.blacklistedUsernames || [],
      totalActivityChecked:
        data.totalActivityChecked !== undefined
          ? data.totalActivityChecked
          : session.progress.totalActivityChecked || 0,
      totalActive: data.totalActive !== undefined ? data.totalActive : session.progress.totalActive || 0,
      totalInactive:
        data.totalInactive !== undefined ? data.totalInactive : session.progress.totalInactive || 0,
      totalFailed: data.totalFailed !== undefined ? data.totalFailed : session.progress.totalFailed || 0,
      totalCountryChecked:
        data.totalCountryChecked !== undefined
          ? data.totalCountryChecked
          : session.progress.totalCountryChecked || 0,
      totalCountryAccepted:
        data.totalCountryAccepted !== undefined
          ? data.totalCountryAccepted
          : session.progress.totalCountryAccepted || 0,
      totalCountryRejected:
        data.totalCountryRejected !== undefined
          ? data.totalCountryRejected
          : session.progress.totalCountryRejected || 0,
      totalCountryFailed:
        data.totalCountryFailed !== undefined
          ? data.totalCountryFailed
          : session.progress.totalCountryFailed || 0,
      currentUsername:
        data.currentUsername !== undefined ? data.currentUsername : session.progress.currentUsername,
      currentUsernameFollowersCount:
        data.currentUsernameFollowersCount !== undefined
          ? data.currentUsernameFollowersCount
          : session.progress.currentUsernameFollowersCount || 0,
      currentUsernameScraped:
        data.currentUsernameScraped !== undefined
          ? data.currentUsernameScraped
          : session.progress.currentUsernameScraped || 0,
      withAvatar: data.withAvatar !== undefined ? data.withAvatar : session.progress.withAvatar || 0,
      verified: data.verified !== undefined ? data.verified : session.progress.verified || 0,
      canDm: data.canDm !== undefined ? data.canDm : session.progress.canDm || 0,
      usernameStatus:
        data.usernameStatus !== undefined
          ? { ...(session.progress.usernameStatus || {}), ...data.usernameStatus }
          : session.progress.usernameStatus || {}
    },
    results: data.results !== undefined ? data.results : session.results || [],
    status:
      data.status === 'completed' || data.status === 'stopped' || data.status === 'paused'
        ? data.status
        : session.status
  });
}

export function applyScraperProgressBatchToSessions(sessions, updates) {
  if (!updates?.length) return sessions;
  return sessions.map((session) => {
    let next = session;
    for (const data of updates) {
      if (data.sessionId === session.sessionId) {
        next = applyScraperProgressToSession(next, data);
      }
    }
    return next;
  });
}
